/**
 * Format file size from bytes to human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Get file extension from filename
 * @param filename - Name of the file
 * @returns File extension without dot
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop()?.toLowerCase() || "" : "";
}

/**
 * Check if file type is allowed
 * @param filename - Name of the file
 * @param allowedExtensions - Array of allowed extensions
 * @returns Boolean indicating if file type is allowed
 */
export function isFileTypeAllowed(
  filename: string,
  allowedExtensions: string[]
): boolean {
  const extension = getFileExtension(filename);
  return allowedExtensions.includes(extension);
}

/**
 * Generate unique filename if duplicate exists
 * @param filename - Original filename
 * @param existingFilenames - Array of existing filenames
 * @returns Unique filename
 */
export function generateUniqueFilename(
  filename: string,
  existingFilenames: string[]
): string {
  if (!existingFilenames.includes(filename)) {
    return filename;
  }

  const nameParts = filename.split(".");
  const extension = nameParts.length > 1 ? nameParts.pop() : "";
  const baseName = nameParts.join(".");

  let counter = 1;
  let newFilename = "";

  do {
    newFilename = extension
      ? `${baseName} (${counter}).${extension}`
      : `${baseName} (${counter})`;
    counter++;
  } while (existingFilenames.includes(newFilename));

  return newFilename;
}

/**
 * Download a file from a URL with proper filename preservation
 * @param url - The URL to download from
 * @param filename - The desired filename for the download
 * @returns Promise that resolves when download starts or rejects on error
 */
export async function downloadFile(
  url: string,
  filename: string
): Promise<void> {
  try {
    if (!url) {
      throw new Error("Download URL is required");
    }

    // For GCS URLs (both public and signed), use direct anchor element approach
    // This avoids CORS issues since we're not fetching the content
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    // Append to body, click, and cleanup
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Download error:", error);
    throw new Error(
      `Failed to download file: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Download a file by opening in a new tab (simpler approach for public URLs)
 * This is useful when you want the browser to handle the download natively
 * @param url - The URL to download from
 */
export function downloadFileInNewTab(url: string): void {
  if (!url) {
    throw new Error("Download URL is required");
  }

  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.click();
}
